import React, { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import { usePolarisRecaptcha } from "../hooks/usePolarisRecaptcha";
import { useNavigate } from "react-router-dom";
import { buildApiUrl, buildOAuthUrl } from "../utils/apiConfig";
import { formatRetryAfter, parseRetryAfterSeconds } from "../utils/loginThrottle";
import { clearAuthStorage, isAthlete, isGuardian, isPolaris } from "../utils/auth";
import { completeLoginSession } from "../utils/loginCompletion";
import {
    consumeLoginNext,
    isForcedLoginSearch,
    loginRoleFromSources,
    persistLoginNextFromSearch,
    resolvePostLoginPath,
} from "../utils/loginRedirect";
import { resolveGuardianPostLoginPath } from "../utils/viewedAthlete";
import { useAuth } from "../contexts/AuthContext";
import {
    ensureLoginScreenHeroReady,
    getPrimedLoginScreenConfig,
} from "../services/loginScreenService";
import { LoginScreenConfig } from "../types/loginScreen";
import { LoginResponse } from "../types/guardian";
import { RecaptchaDisclosure } from "./RecaptchaDisclosure";
import defaultBg from "../images/login/login-background.jpg";
import polarisLogo from "../images/Polaris Logo.svg";
import "../styles/login-page.css";
import "../styles/google-button.css";
import "../styles/clever-button.css";

type Props = {
    /** Optional: override the background image (URL or imported asset) */
    backgroundImageUrl?: string;
};

function resolveErrorMessage(errorCode: string): string {
    switch (errorCode) {
        case "clever_auth_failed":
            return "Clever login failed. Please try again or contact support.";
        case "no_active_subscription":
            return "No active Polaris subscription was found for your school. Please contact your administrator.";
        case "clever_not_provisioned":
            return "Your school has not yet set up your Polaris account. Please ask your coach or administrator to provision you, then try signing in with Clever again.";
        case "clever_email_mismatch":
            return "The email Clever returned does not match the address your school used when your account was provisioned. Ask your administrator to confirm your school email, then try Clever again.";
        case "auth_failed":
            return "Authentication failed. Please try again.";
        case "auth_refresh_failed":
            return "Your session could not be verified. Please log in again.";
        case "unauthorized":
            return "Please sign in to continue.";
        case "mfa_locked":
            return "Too many incorrect codes. Please log in again to receive a new code.";
        case "mfa_expired":
            return "Your verification code expired. Please log in again to receive a new code.";
        default:
            return errorCode;
    }
}

export function LoginPage({ backgroundImageUrl }: Props) {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(true);
    const [error, setError] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        // Session-swap / expiry redirects (see utils/sessionGuard.ts and apiClient.ts)
        // land here with a reason flag; explain them plainly instead of leaving the user
        // guessing after a forced sign-out.
        if (params.get("session_changed") === "true") {
            return "You were signed out because a different account signed in on this browser " +
                "(for example, in another incognito or browser window). Please log in again.";
        }
        if (params.get("session_expired") === "true") {
            return "Your session has expired. Please log in again.";
        }
        const errorCode = params.get("error") || "";
        return resolveErrorMessage(errorCode);
    });
    const [successMessage] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("password_updated") === "true") {
            return "Your password was updated. Sign in with your new password.";
        }
        return "";
    });
    const [loginConfig, setLoginConfig] = useState<LoginScreenConfig | null>(() => getPrimedLoginScreenConfig());
    const [hasConfigError, setHasConfigError] = useState(false);
    /** After the login-screen API attempt finishes (used only to decide bundled fallback vs none). */
    const [configSettled, setConfigSettled] = useState(false);
    /** Form + hero paint together once the CMS hero is decoded (or primed before navigation). */
    const [isLoginShellReady, setIsLoginShellReady] = useState(
        () => Boolean(backgroundImageUrl) || Boolean(getPrimedLoginScreenConfig()?.heroImageUrl),
    );
    const navigate = useNavigate();
    const { refreshAuth } = useAuth();
    const { executeRecaptcha } = usePolarisRecaptcha();

    // Check if user is already authenticated and redirect
    useEffect(() => {
        persistLoginNextFromSearch(window.location.search);

        if (isForcedLoginSearch(window.location.search)) {
            clearAuthStorage();
            return;
        }

        const checkAuth = async () => {
            const userId = localStorage.getItem('userId');

            if (userId) {
                // User has userId in localStorage, but we need to verify the session is still valid
                try {
                    const response = await fetch(buildApiUrl('api/auth/check'), {
                        method: 'GET',
                        credentials: 'include', // Send HTTP-only cookies
                    });

                    if (response.ok) {
                        const data = await response.json();

                        // Session is valid, redirect to appropriate page.
                        // /api/auth/check returns { status, userInfo: { authenticated } }.
                        const sessionOk = data?.authenticated === true
                            || data?.status === "authenticated"
                            || data?.userInfo?.authenticated === true;
                        if (sessionOk) {
                            await refreshAuth();
                            if (!localStorage.getItem("userId")) {
                                return;
                            }
                            const next = consumeLoginNext();
                            const role = loginRoleFromSources({
                                role: localStorage.getItem("userRole"),
                                isGuardian: isGuardian(),
                                isPolaris: isPolaris(),
                                isAthlete: isAthlete(),
                            });
                            const hasProfile = localStorage.getItem("hasProfile") === "true";
                            const path = role === "GUARDIAN"
                                ? resolveGuardianPostLoginPath(next)
                                : resolvePostLoginPath(role, next, hasProfile);
                            if (path === "/add-admin-profile") {
                                navigate(path, { replace: true, state: { firstTimeSetup: true } });
                                return;
                            }
                            navigate(path, { replace: true });
                        } else {
                            // Session expired, clear localStorage
                            localStorage.removeItem('userId');
                            localStorage.removeItem('hasProfile');
                            localStorage.removeItem('userRole');
                        }
                    } else if (response.status === 401) {
                        // Session expired, clear localStorage
                        localStorage.removeItem('userId');
                        localStorage.removeItem('hasProfile');
                        localStorage.removeItem('userRole');
                    }
                } catch (error) {
                    // Network error or backend down, allow user to login
                }
            }
        };

        checkAuth();
    }, [navigate, refreshAuth]);

    useEffect(() => {
        if (backgroundImageUrl) {
            setIsLoginShellReady(true);
            setConfigSettled(true);
            return;
        }

        let cancelled = false;

        ensureLoginScreenHeroReady()
            .then(({ config, heroReady }) => {
                if (cancelled) {
                    return;
                }
                if (config) {
                    setLoginConfig(config);
                }
                if (config?.heroImageUrl && !heroReady) {
                    setHasConfigError(true);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setHasConfigError(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setConfigSettled(true);
                    setIsLoginShellReady(true);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [backgroundImageUrl]);

    async function handleSubmit(ev: React.FormEvent) {
        ev.preventDefault();
        setError("");

        try {
            if (!executeRecaptcha) {
                setError("Security verification is still loading. Please try again in a moment.");
                return;
            }
            const recaptchaToken = await executeRecaptcha("login");
            const res = await fetch(buildApiUrl("api/auth/login"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ username, password, recaptchaToken }),
            });

            const data: LoginResponse | Record<string, unknown> = await res.json().catch(() => ({} as LoginResponse));

            if (!res.ok) {
                if (res.status === 429) {
                    const retryAfterSeconds = parseRetryAfterSeconds(
                        data as Record<string, unknown>,
                        res.headers.get("Retry-After")
                    );
                    setError(
                        retryAfterSeconds
                            ? `Too many login attempts. ${formatRetryAfter(retryAfterSeconds)}`
                            : "Too many login attempts. Please try again later."
                    );
                    return;
                }
                const detail = String(
                    (data as Record<string, unknown>)?.detail ??
                        (data as Record<string, unknown>)?.message ??
                        ""
                );
                if (res.status === 400 && /recaptcha/i.test(detail)) {
                    setError(
                        "Security verification did not pass. Refresh the page and try again, or wait a minute and retry."
                    );
                    return;
                }
                setError("Invalid email or password.");
                return;
            }

            const loginData = data as LoginResponse;

            // Second-and-subsequent password logins must verify an emailed code before a session
            // is issued. The backend returns no cookie here; challenge metadata is kept in
            // sessionStorage (never in the URL) and consumed by the MFA verification screen.
            if (loginData.mfaRequired && loginData.mfaChallengeId) {
                sessionStorage.setItem("mfaChallengeId", loginData.mfaChallengeId);
                sessionStorage.setItem("mfaMaskedEmail", loginData.maskedEmail ?? "");
                if (loginData.expiresInSeconds != null) {
                    sessionStorage.setItem("mfaExpiresInSeconds", String(loginData.expiresInSeconds));
                }
                navigate("/login/mfa");
                return;
            }

            await completeLoginSession(loginData, navigate, refreshAuth);
        } catch {
            setError("Something went wrong. Please try again.");
        }
    }


    const bgColor = loginConfig?.backgroundColor ?? '#000000';
    const cmsHero =
        loginConfig?.heroImageUrl && !hasConfigError ? loginConfig.heroImageUrl : undefined;
    const useDefaultHero =
        configSettled && !backgroundImageUrl && !cmsHero && (hasConfigError || !loginConfig?.heroImageUrl);
    const bg = backgroundImageUrl ?? cmsHero ?? (useDefaultHero ? defaultBg : undefined);
    const googleHref = buildOAuthUrl("google");

    /**
     * Initiates Clever district SSO with OAuth CSRF protection: backend sets HttpOnly state cookie
     * and redirects to Clever authorize (see GET /api/auth/clever/start).
     */
    function handleCleverLogin() {
        window.location.href = buildApiUrl("api/auth/clever/start");
    }

    const loginShellStyle = {
        ...(bg ? { ['--login-bg' as any]: `url(${bg})` } : {}),
        ['--login-bg-color' as any]: bgColor,
    };

    if (!isLoginShellReady && !backgroundImageUrl) {
        return (
            <main
                className="login-page login-loading"
                style={loginShellStyle}
                data-testid="login-loading"
                aria-busy="true"
            />
        );
    }

    return (
        <main
            className="login-page"
            style={loginShellStyle}
            data-testid="login-page"
        >
            <div className="login-overlay" style={{ backgroundColor: `${bgColor}66` }} />

            <section className="login-card" role="dialog" aria-label="Sign in">
                <header className="login-head">
                    <img
                        src={polarisLogo}
                        alt="Polaris Athlete"
                        className="login-logo"
                    />
                    {loginConfig?.tagline ? (
                        <div
                            className="tagline tagline-dynamic"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(loginConfig.tagline) }}
                            data-testid="login-tagline"
                        />
                    ) : (
                        <div className="login-tagline-wrapper" data-testid="login-tagline">
                            <h2 className="tagline-main">College Counseling x NIL Education.</h2>
                            <h3 className="tagline-sub">Connected.</h3>
                        </div>
                    )}
                    <p className="welcome">Welcome back! Please login to your account.</p>
                </header>

                <form onSubmit={handleSubmit} className="login-form">
                    {successMessage && <div className="form-alert is-ok">{successMessage}</div>}
                    {error && <div className="form-alert is-error">{error}</div>}

                    <div className="field">
                        <label htmlFor="email">Email Address</label>
                        <input
                            id="email"
                            type="email"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="name@example.com"
                            required
                            autoComplete="username"
                            className="input"
                        />
                    </div>

                    <div className="field">
                        <label htmlFor="password">Password</label>
                        <input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            autoComplete="current-password"
                            className="input"
                        />
                    </div>

                    <div className="row row-meta">
                        <label className="remember">
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                            />
                            Remember Me
                        </label>
                        <a href="/login/forgot-password" className="login-forgot-link">
                            Forgot password?
                        </a>
                    </div>

                    <button type="submit" className="login-btn-primary">Sign in</button>

                    <div className="row row-oauth">
                        <button
                            type="button"
                            onClick={handleCleverLogin}
                            className="btn-clever"
                            aria-label="Log in with Clever"
                        >
                            <svg width="20" height="20" viewBox="0 0 40 40" aria-hidden="true" focusable="false">
                                <rect width="40" height="40" rx="6" fill="#4274F6"/>
                                <text x="50%" y="54%" dominantBaseline="middle" textAnchor="middle" fill="#fff" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="22">C</text>
                            </svg>
                            <span>Log in with Clever</span>
                        </button>

                        <a href={googleHref} className="btn-google" data-provider="google" aria-label="Sign in with Google">
                            <svg viewBox="0 0 48 48" aria-hidden width="18" height="18">
                                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 31.7 29.3 35 24 35 16.8 35 11 29.2 11 22S16.8 9 24 9c3.2 0 6.1 1.2 8.3 3.2l5.7-5.7C34.3 3 29.4 1 24 1 10.8 1 0 11.8 0 25s10.8 24 24 24c12.2 0 22.4-8.9 24-21h-4.4z"/>
                                <path fill="#FF3D00" d="M0 25c0 7.1 3.8 13.3 9.6 16.7l7.1-5.8C13 34 11 29.8 11 25c0-4.8 2-9 5.7-10.9L9.6 8.3C3.8 11.7 0 17.9 0 25z"/>
                                <path fill="#4CAF50" d="M24 49c6.5 0 12-2.1 16.4-5.8l-7.6-6C30.6 39.7 27.5 41 24 41c-5.3 0-9.7-3.3-11.3-8H5.3l-7.1 5.8C4.3 39.7 13.3 47 24 47z"/>
                                <path fill="#1976D2" d="M48 25c0-1.6-.2-3.2-.6-4.7H24v9h13.5C36.5 32.2 32.9 35 28 35c-5 0-9.1-3.5-10.5-8h-8.2l-7.1 5.8C4.3 39.7 13.3 47 24 47 37.2 47 48 36.2 48 23z"/>
                            </svg>
                            <span>Log in with Google</span>
                        </a>
                    </div>
                    <RecaptchaDisclosure className="recaptcha-disclosure--on-dark" />
                </form>

                <footer className="login-footer">
                    <p className="login-footer-copyright">© 2026 Polaris Athlete LLC. All rights reserved.</p>
                    <div className="login-footer-links">
                        <a href="/privacy" className="login-footer-link" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                        <span className="login-footer-separator">|</span>
                        <a href="/terms" className="login-footer-link" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>
                        <span className="login-footer-separator">|</span>
                        <a href="/data" className="login-footer-link" target="_blank" rel="noopener noreferrer">Data & Account Deletion</a>
                        <span className="login-footer-separator">|</span>
                        <a href="mailto:support@polarisathlete.com" className="login-footer-link">Contact Us</a>
                    </div>
                    <a href="mailto:support@polarisathlete.com" className="login-footer-email">support@polarisathlete.com</a>
                </footer>
            </section>
        </main>
    );
}

export default LoginPage;